// NotificationPanel.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import {
    Bell,
    Briefcase,
    BookOpen,
    Clock,
    MessageSquare,
    Calendar,
    ChevronLeft,
    CheckCheck,
    X,
    Loader2
} from "lucide-react";
import { useNotificationsInfinite, useMarkAllNotificationsRead, useMarkNotificationRead } from "../hooks/query/useNotifications";

export const NotificationPanel = ({ trigger }) => {
    const [isOpen, setIsOpen] = useState(false);
    const panelRef = useRef();
    const observerTarget = useRef(null);
    const navigate = useNavigate(); // Initialize navigate

    const markAllMutation = useMarkAllNotificationsRead();
    const markReadMutation = useMarkNotificationRead();

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (window.innerWidth < 768) {
            document.body.style.overflow = isOpen ? "hidden" : "unset";
        }
        return () => { document.body.style.overflow = "unset"; };
    }, [isOpen]);

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError
    } = useNotificationsInfinite();

    // Infinite scroll observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage) {
                    fetchNextPage();
                }
            },
            { threshold: 1.0, root: panelRef.current }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current);
            }
        };
    }, [hasNextPage, fetchNextPage, isOpen]);

    const allNotifications = data?.pages.flatMap((page) => page?.data?.notifications || []) || [];
    const unreadCount = data?.pages[0]?.data?.unreadCount || 0;

    const getIcon = (category) => {
        switch (category) {
            case "courses": return { icon: BookOpen, color: "text-blue-600", bg: "bg-blue-50" };
            case "jobs": return { icon: Briefcase, color: "text-emerald-600", bg: "bg-emerald-50" };
            case "discussions": return { icon: MessageSquare, color: "text-indigo-600", bg: "bg-indigo-50" };
            case "admin": return { icon: Bell, color: "text-amber-600", bg: "bg-amber-50" };
            case "events": return { icon: Calendar, color: "text-rose-600", bg: "bg-rose-50" };
            default: return { icon: Bell, color: "text-gray-600", bg: "bg-gray-50" };
        }
    };

    const handleNotificationClick = (note) => {
        // 1. Mark as read if not already
        if (!note.isRead) {
            markReadMutation.mutate(note._id);
        }

        // 2. Navigate based on category
        setIsOpen(false); // Close panel
        const { category, data } = note;

        if (!data) return;

        console.log("Navigating for:", category, data);

        switch (category) {
            case 'discussions':
                const discussionId = data.discussionId || data.id || data._id;
                if (discussionId) {
                    navigate(`/discussions?discussionId=${discussionId}`);
                }
                break;
            case 'jobs':
                const jobId = data.jobId || data.id || data._id;
                if (jobId) {
                    navigate(`/jobs/${jobId}`);
                }
                break;
            case 'courses':
                const courseId = data.courseId || data.id || data._id;
                if (courseId) {
                    navigate(`/courses/${courseId}`);
                }
                break;
            case 'events':
                // No route known yet for events
                break;
            // Add other cases as needed
            default:
                console.warn("Unknown notification category:", category);
                break;
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return "";
        const date = new Date(timestamp.toString().length === 10 ? timestamp * 1000 : timestamp);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return "Just now";
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="relative inline-flex">
            {/* Trigger (your custom bell) */}
            <div className="relative inline-flex items-center" onClick={() => setIsOpen(!isOpen)}>
                {trigger}
                {unreadCount > 0 && (
                    <div className="absolute -top-0.5 -right-2 flex h-5 w-auto min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white pointer-events-none">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </div>
                )}
            </div>

            {/* Notification Panel */}
            {isOpen && (
                <div
                    ref={panelRef}
                    className="
                        fixed inset-0 z-[100] w-full h-full bg-slate-50 flex flex-col 
                        md:absolute md:inset-auto md:right-0 md:mt-2 md:w-96 md:h-auto md:max-h-[400px] 
                        md:bg-white md:rounded-2xl md:shadow-xl md:border md:border-gray-100 
                        dark:bg-gray-900 dark:md:border-gray-700 animate-in fade-in zoom-in-95 duration-200
                    "
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-4 md:py-3 bg-white md:bg-transparent border-b border-gray-100 dark:border-gray-700 shrink-0 sticky top-0 z-10 shadow-sm md:shadow-none">
                        <div className="flex items-center gap-3">
                            {/* Mobile Close Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpen(false);
                                }}
                                className="md:hidden p-2 -ml-2 text-gray-500 hover:bg-gray-50 rounded-full transition-colors"
                            >
                                <ChevronLeft className="w-6 h-6" />
                            </button>
                            <h3 className="text-lg md:text-base font-semibold text-gray-800 dark:text-gray-200">
                                Notifications
                            </h3>
                            {unreadCount > 0 && (
                                <span className="bg-red-100 text-red-600 text-[10px] font-semibold px-2 py-0.5 rounded-full md:hidden">
                                    {unreadCount} New
                                </span>
                            )}
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                markAllMutation.mutate();
                            }}
                            disabled={markAllMutation.isPending}
                            className={`text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1.5 active:scale-95 transition-transform ${markAllMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <CheckCheck className="w-4 h-4" />
                            <span className="hidden md:inline">Mark all read</span>
                            <span className="md:hidden">Read All</span>
                        </button>
                    </div>

                    {/* List */}
                    <div className="flex-1 overflow-y-auto p-3 md:p-0 scrollbar-thin scrollbar-thumb-gray-200">
                        {isLoading ? (
                            <div className="flex justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                            </div>
                        ) : isError ? (
                            <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                                <p className="text-sm text-red-500 mb-2">Failed to load notifications</p>
                                <button
                                    onClick={() => fetchNextPage({ pageParam: 1 })}
                                    className="text-xs text-blue-600 hover:underline"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : allNotifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400">
                                <Bell className="w-8 h-8 mb-2 opacity-50" />
                                <p className="text-sm">No notifications yet</p>
                            </div>
                        ) : (
                            <ul className="flex flex-col">
                                {allNotifications.map((note) => {
                                    const { icon: Icon, color, bg } = getIcon(note.category || 'info');
                                    return (
                                        <li
                                            key={note._id}
                                            className={`
                                                relative group flex gap-4 p-4 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-800 last:border-none
                                                ${!note.isRead
                                                    ? 'bg-blue-50/40 dark:bg-blue-900/10'
                                                    : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'
                                                }
                                            `}
                                            onClick={() => handleNotificationClick(note)}
                                        >
                                            {/* Icon */}
                                            <div className={`w-10 h-10 rounded-full ${bg} flex items-center justify-center shrink-0`}>
                                                <Icon className={`w-5 h-5 ${color}`} />
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2 mb-1">
                                                    <p className={`text-sm ${!note.isRead ? 'font-semibold text-gray-900 dark:text-gray-100' : 'font-medium text-gray-700 dark:text-gray-300'}`}>
                                                        {note.title}
                                                    </p>
                                                    <span className="text-[10px] text-gray-400 whitespace-nowrap">
                                                        {formatTime(note.createdAt)}
                                                    </span>
                                                </div>
                                                <p className={`text-xs leading-relaxed line-clamp-2 ${!note.isRead ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}`}>
                                                    {note.message}
                                                </p>
                                            </div>

                                            {/* Unread Indicator (Right aligned blue dot) */}
                                            {!note.isRead && (
                                                <div className="self-center shrink-0">
                                                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600 ring-4 ring-blue-50 dark:ring-blue-900/50" />
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                                {/* Sentinel for Infinite Scroll */}
                                <div ref={observerTarget} className="h-4 flex justify-center items-center">
                                    {isFetchingNextPage && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                                </div>
                            </ul>
                        )}

                        {/* Mobile Bottom Spacer for reachability */}
                        <div className="h-6 md:hidden"></div>
                    </div>
                </div>
            )}
        </div>
    );
};
